import React from 'react';
import { Film, Target, Clock, Award, Users, Star, Heart, Play, Camera, Video, Sparkles, TrendingUp, CheckCircle, MessageCircle } from 'lucide-react';

function AboutSection() {
    const features = [
        {
            icon: <Film size={24} />,
            title: "Full Production Cycle",
            description: "From pre-production to post-production - complete solutions"
        },
        {
            icon: <Target size={24} />,
            title: "Target Audience Focus",
            description: "Conceptualize keeping your target audience in mind"
        },
        {
            icon: <Clock size={24} />,
            title: "On-Time Delivery",
            description: "Deliver within budget and on schedule, every time"
        },
        {
            icon: <Award size={24} />,
            title: "Beyond Expectations",
            description: "We believe in delivering beyond client expectations"
        }
    ];

    const teamHighlights = [
        {
            icon: <Users size={20} />,
            text: "Creative Directors"
        },
        {
            icon: <Camera size={20} />,
            text: "Cinematographers"
        },
        {
            icon: <Video size={20} />,
            text: "Editors & VFX Artists"
        },
        {
            icon: <MessageCircle size={20} />,
            text: "Script Writers"
        }
    ];

    const testimonials = [
        {
            text: "Kaanshi Films transformed our brand vision into cinematic excellence. Their attention to detail is remarkable.",
            author: "Rajiv Mehta",
            role: "Marketing Director, Luxe Brands",
            rating: 5
        },
        {
            text: "Professional, creative, and delivered beyond our expectations. The team truly understands storytelling.",
            author: "Priya Sharma",
            role: "Founder, Creative Studio",
            rating: 5
        }
    ];

    return (
        <div className="bg-white py-16 md:py-24">
            {/* Hero Pattern Background */}
            <div className="absolute inset-0 -z-10 h-[600px] w-full bg-gradient-to-br from-emerald-50/50 via-transparent to-transparent" />
            
            <div className="max-w-7xl mx-auto px-4">
                {/* Main Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
                    {/* Left Content */}
                    <div className="space-y-6">
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <span className="inline-block px-4 py-2 bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-700 rounded-full text-sm font-medium">
                                    About Us
                                </span>
                                <span className="flex items-center gap-1 text-emerald-600 text-sm">
                                    <Sparkles size={16} />
                                    Est. 2015
                                </span>
                            </div>
                            
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
                                We Don't Just Make Videos,{' '}
                                <span className="bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">
                                    We Tell Stories
                                </span>
                            </h2>
                            
                            <div className="prose prose-lg text-gray-600">
                                <p className="leading-relaxed">
                                    <span className="font-semibold text-gray-900">Kaanshi Films Entertainment (KFE)</span> is a Mumbai based Video & Film production house. 
                                    We add values to videos by our creative thoughts, ideation, professional scripting, 
                                    shooting, Editing, VFX & SFX.
                                </p>
                                <p className="leading-relaxed">
                                    We are a team of some of the most creative minds of the industry. We are not only 
                                    video or film makers, we are <span className="font-semibold text-emerald-600">communicative film makers</span>, so that we can convey the 
                                    message to our target audience successfully.
                                </p>
                            </div>
                            
                            {/* Vision/Mission Cards */}
                            <div className="flex flex-col sm:flex-row gap-4 mt-4">
                                <div className="flex-1 p-4 bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl border border-emerald-100">
                                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-500 rounded-lg flex items-center justify-center mb-3">
                                        <Heart size={20} className="text-white" />
                                    </div>
                                    <h4 className="font-semibold text-gray-900 mb-1">Our Vision</h4>
                                    <p className="text-sm text-gray-600">To be the most trusted creative partner for brands seeking cinematic excellence.</p>
                                </div>
                                <div className="flex-1 p-4 bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl border border-emerald-100">
                                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-500 rounded-lg flex items-center justify-center mb-3">
                                        <Target size={20} className="text-white" />
                                    </div>
                                    <h4 className="font-semibold text-gray-900 mb-1">Our Mission</h4>
                                    <p className="text-sm text-gray-600">Create impactful visual stories that connect brands with their audience.</p>
                                </div>
                            </div>
                        </div>
                        
                        {/* Features Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {features.map((feature, index) => (
                                <div key={index} className="group p-4 rounded-xl border border-gray-100 hover:border-emerald-200 hover:bg-emerald-50/50 transition-all duration-300">
                                    <div className="flex items-start gap-4">
                                        <div className="p-2 bg-gradient-to-br from-emerald-100 to-green-100 rounded-lg group-hover:scale-110 transition-transform duration-300">
                                            <div className="text-emerald-600">{feature.icon}</div>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-1 group-hover:text-emerald-700 transition-colors">
                                                {feature.title}
                                            </h4>
                                            <p className="text-sm text-gray-600">{feature.description}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right Content - Enhanced Stats */}
                    <div className="space-y-8">
                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 gap-6">
                            <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-6 rounded-2xl hover:shadow-lg transition-shadow">
                                <div className="text-4xl font-bold bg-gradient-to-r from-emerald-700 to-green-600 bg-clip-text text-transparent mb-2">
                                    20+
                                </div>
                                <p className="text-gray-600">Years Experience</p>
                                <div className="mt-2 w-full h-1 bg-gradient-to-r from-emerald-200 to-green-200 rounded-full">
                                    <div className="w-4/5 h-full bg-gradient-to-r from-emerald-500 to-green-500 rounded-full" />
                                </div>
                            </div>
                            <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-6 rounded-2xl hover:shadow-lg transition-shadow">
                                <div className="text-4xl font-bold bg-gradient-to-r from-emerald-700 to-green-600 bg-clip-text text-transparent mb-2">
                                    500+
                                </div>
                                <p className="text-gray-600">Projects Completed</p>
                                <div className="mt-2 w-full h-1 bg-gradient-to-r from-emerald-200 to-green-200 rounded-full">
                                    <div className="w-full h-full bg-gradient-to-r from-emerald-500 to-green-500 rounded-full" />
                                </div>
                            </div>
                            <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-6 rounded-2xl hover:shadow-lg transition-shadow">
                                <div className="text-4xl font-bold bg-gradient-to-r from-emerald-700 to-green-600 bg-clip-text text-transparent mb-2">
                                    50+
                                </div>
                                <p className="text-gray-600">Happy Clients</p>
                                <div className="mt-2 flex gap-1">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} size={16} className="fill-yellow-400 text-yellow-400" />
                                    ))}
                                </div>
                            </div>
                            <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-6 rounded-2xl hover:shadow-lg transition-shadow">
                                <div className="text-4xl font-bold bg-gradient-to-r from-emerald-700 to-green-600 bg-clip-text text-transparent mb-2">
                                    24/7
                                </div>
                                <p className="text-gray-600">Support</p>
                                <div className="mt-2 flex items-center gap-1 text-emerald-600">
                                    <CheckCircle size={16} />
                                    <span className="text-xs">Always Available</span>
                                </div>
                            </div>
                        </div>
                        
                        {/* Team Section */}
                        <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <Users size={20} className="text-emerald-600" />
                                Meet Our Creative Team
                            </h3>
                            <div className="grid grid-cols-2 gap-3">
                                {teamHighlights.map((item, index) => (
                                    <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                                        <div className="text-emerald-600">{item.icon}</div>
                                        <span className="text-sm text-gray-700">{item.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        {/* Promise Card */}
                        <div className="bg-gradient-to-r from-emerald-600 to-green-600 rounded-2xl p-8 text-white relative overflow-hidden">
                            {/* Decorative Elements */}
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-8 -mt-8" />
                            <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/10 rounded-full -ml-8 -mb-8" />
                            
                            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                                <Award size={24} />
                                Our Promise
                            </h3>
                            <p className="leading-relaxed relative z-10">
                                With a highly dynamic working process, we conceptualize keeping your target audience 
                                in mind, design within your brand values & guidelines and communicate through a clear 
                                vision, ensuring that you receive the most satisfiable output.
                            </p>
                            
                            {/* Achievement Tags */}
                            <div className="flex flex-wrap gap-3 mt-6">
                                <span className="px-3 py-1 bg-white/20 rounded-full text-sm backdrop-blur-sm">
                                    100% Client Satisfaction
                                </span>
                                <span className="px-3 py-1 bg-white/20 rounded-full text-sm backdrop-blur-sm">
                                    Award Winning Team
                                </span>
                                <span className="px-3 py-1 bg-white/20 rounded-full text-sm backdrop-blur-sm">
                                    Pan India Service
                                </span>
                            </div>
                        </div>

                    </div>
                </div>
                        <div className="space-y-3">
                            <h3 className="text-lg font-semibold text-gray-900">What Clients Say</h3>
                           <div className="grid col-span-1 md:grid-cols-2 gap-2">
                             {testimonials.map((testimonial, index) => (
                                <div key={index} className="bg-white border border-gray-100 rounded-xl p-4 hover:shadow-md transition-shadow">
                                    <div className="flex items-center gap-2 mb-2">
                                        {[...Array(testimonial.rating)].map((_, i) => (
                                            <Star key={i} size={16} className="fill-yellow-400 text-yellow-400" />
                                        ))}
                                    </div>
                                    <p className="text-sm text-gray-600 italic mb-2">"{testimonial.text}"</p>
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                                            {testimonial.author.charAt(0)}
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-900">{testimonial.author}</p>
                                            <p className="text-xs text-gray-500">{testimonial.role}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                           </div>
                        </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 pt-12 border-t border-gray-100">
                    <div className="text-center">
                        <div className="text-2xl font-bold text-emerald-700">100+</div>
                        <p className="text-sm text-gray-600">Corporate Films</p>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-emerald-700">50+</div>
                        <p className="text-sm text-gray-600">Music Videos</p>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-emerald-700">200+</div>
                        <p className="text-sm text-gray-600">TV Commercials</p>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-emerald-700">150+</div>
                        <p className="text-sm text-gray-600">Digital Content</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AboutSection;